import BluebirdPromise from 'bluebird';
import download from 'downloadjs';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import FullPageLoadingIndicator from '../../../shared/components/FullPageLoadingIndicator';
import Page from '../../../shared/components/Page';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../shared/components/VerbButton';
import BadgesSection from './BadgesSection';
import RecommendedPathwaysSection from './RecommendedPathwaysSection';

import {
    getBadge,
    getManyPathways,
    getManyTeams,
    getManyUserPathways,
    getPathway,
    getUserPathway,
    getUserPathwayComplete,
    putUserPathway
} from '../../../shared/actions/index';
import Image from '../../../shared/components/Image';
import VerbHeading from '../../../shared/components/VerbHeading';
import VerbText from '../../../shared/components/VerbText';
import { makeGetObjectsFromStateByFunction, makeGetObjectsFromStateById } from '../../../shared/selectors/index';
import { createUserCertificate, IMAGE_PROPS } from '../../../shared/utils/ImageUtils';

import { RuntimeContext } from '../../../../common/RuntimeContext';
import { LICENSE_TYPES, SEGMENT_EVENTS } from '../../../shared/Constants';

export class PathwayInterstitial extends Component {
    static propTypes = {
        match: PropTypes.object,
        getUserPathway: PropTypes.func,
        putUserPathway: PropTypes.func,
        userPathway: PropTypes.object,
        collectionsLoading: PropTypes.bool,
        pathwayLoading: PropTypes.bool,
        teamsById: PropTypes.object,
        loggedInUser: PropTypes.object,
        isVerbAdmin: PropTypes.bool,
        loggedInUserCompany: PropTypes.object,
        getPathway: PropTypes.func,
        pathway: PropTypes.object,
        getManyPathways: PropTypes.func,
        viewablePathways: PropTypes.arrayOf(PropTypes.object),
        getManyUserPathways: PropTypes.func,
        getManyTeams: PropTypes.func,
        isAccountOwner: PropTypes.bool,
        userPathways: PropTypes.array,
        history: PropTypes.object.isRequired
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            modalOpen: false,
            loading: true,
            recommendationsSent: false
        };
    }

    componentDidMount() {
        const { getUserPathway, getPathway, getManyPathways, getManyUserPathways, getManyTeams, match } = this.props;
        const { userPathwayId } = match.params;

        const promises = [
            getUserPathway({ userPathwayId, pathsContext: this.context.pathsWeb }),
            getUserPathwayComplete({ userPathwayId, pathsContext: this.context.pathsWeb }),
            getManyPathways({ pathsContext: this.context.pathsWeb }),
            getManyTeams({ pathsContext: this.context.pathsWeb }),
            getManyUserPathways({ pathsContext: this.context.pathsWeb })
        ];

        BluebirdPromise.all(promises).spread((userPathway, userPathwayComplete) => {
            const secondaryPromises = [
                getPathway({ pathwayId: userPathway.pathwayId, pathsContext: this.context.pathsWeb }),
                getManyUserPathways({ userProfileId: userPathway.userProfileId, pathsContext: this.context.pathsWeb })
            ];

            return BluebirdPromise.all(secondaryPromises).then(() => {
                this.setState((currentState, props) => {
                    if (!props.collectionsLoading && !props.pathwayLoading) {
                        return {
                            loading: false,
                            userPathwayComplete
                        };
                    }
                    return null;
                });
            });
        });
    }

    _makeUserPathwayThumbnail = () => {
        const { userPathway } = this.props;
        const imageProps = IMAGE_PROPS.pathwayHeadingImageCloudinary;

        return (
            <Image
                src={userPathway.displayGraphic}
                dimension={imageProps.dimension}
                CloudinaryCrop={imageProps.cloudinaryCrop}
                quality={{ q: 100 }}
                className={'trackCardImage col-12 col-sm-6 col-lg-4'}
            />
        );
    };

    _renderCongrats = () => {
        const { userPathway } = this.props;
        const thumbnail = this._makeUserPathwayThumbnail();

        return (
            <div className={'container'}>
                <div className={'congrats-section col-12'}>
                    <div className={'row'}>
                        {thumbnail}
                        <div className={'congrats-container col-10 col-sm-11 col-lg'}>
                            <VerbHeading
                                className={'congrats-header'}
                                defaultMessage={`Holy Cow! You completed {PathwayName} pathway!`}
                                hNumber={1}
                                valuesToInject={{
                                    PathwayName: <span className={'pathway-name'}>{userPathway.name || ''}</span>
                                }}
                            />
                            <div className={'congrats-completion-dots'}>
                                {userPathway.content.map(() => (
                                    <i className={'fa fa-circle complete-dot fa-sm'} aria-hidden={true} />
                                ))}
                                <div className={'complete-percentage'}>100% COMPLETE</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    _downloadCert = (certUrl, fileName) => {
        const { loggedInUserCompany, isVerbAdmin } = this.props;
        return fetch(certUrl)
            .then((res) => res.blob())
            .then((blob) => download(blob, fileName));
    };

    _renderPathwayStats = () => {
        const { userPathway = {}, pathway = {}, loggedInUser } = this.props;
        const { userPathwayComplete } = this.state;
        const userName = `${loggedInUser.firstName || ''} ${loggedInUser.lastName}`;
        const date = userPathway.completedOn || userPathway.updatedOn;
        const certificateUrl = createUserCertificate(userName, pathway.name, date, 'PATHWAY');
        const certFileName = `${userPathway.name.split(' ').join('_')}_Certificate`;
        const calculatTimeToComplete = () => {
            if (userPathwayComplete.timeToComplete > 6) {
                return `${Math.ceil(userPathwayComplete.timeToComplete / 7)} weeks`;
            }
            if (userPathwayComplete.timeToComplete) {
                return `${userPathwayComplete.timeToComplete} days`;
            }
            return '1 day';
        };

        return (
            <div className={'container'}>
                <div className={'pathway-numbers col-12'}>
                    <div className={'stats-card col-12 col-sm-5 col-lg'}>
                        <VerbText
                            messageId={'PATHWAY_INTERSTITIAL_TIME_TO_COMPLETE'}
                            defaultMessage={`You conquered this pathway in {TimeToComplete}`}
                            valuesToInject={{
                                TimeToComplete: <span className={'stats-text'}>{calculatTimeToComplete()}</span>
                            }}
                        />
                    </div>
                    <div className={'stats-card col-12 col-sm-5 col-lg'}>
                        <VerbText
                            messageId={'PATHWAY_INTERSTITIAL_SKILLS_DEVELOPED'}
                            defaultMessage={`You've developed {NumberOfSkills}`}
                            valuesToInject={{
                                NumberOfSkills: (
                                    <span
                                        className={'stats-text'}
                                    >{`${userPathwayComplete.numOfPathwaySkills} skills`}</span>
                                )
                            }}
                        />
                    </div>
                    <div className={'stats-card col-12 col-sm-5 col-lg'}>
                        <VerbText
                            messageId={'PATHWAY_INTERSTITIAL_COLLECTIONS_COMPLETED'}
                            defaultMessage={`You completed {NumberOfCollections}`}
                            valuesToInject={{
                                NumberOfCollections: (
                                    <span
                                        className={'stats-text'}
                                    >{`${userPathwayComplete.numOfCollections} collections`}</span>
                                )
                            }}
                        />
                    </div>
                    <div className={'stats-card col-12 col-sm-5 col-lg'}>
                        <VerbText
                            messageId={'PATHWAY_INTERSTITIAL_CERTIFICATE'}
                            defaultMessage={`You completed {NumberOfCertificates}`}
                            valuesToInject={{
                                NumberOfCertificates: <span className={'stats-text'}>1 pathway</span>
                            }}
                        />
                        <VerbButton
                            buttonText={'PRINT'}
                            className={'print-button'}
                            onClick={() => this._downloadCert(certificateUrl, certFileName)}
                            buttonType={VERB_BUTTON_TYPE.PRIMARY_DEFAULT}
                        />
                    </div>
                </div>
            </div>
        );
    };

    _renderPathwayAssociatedBadges = () => {
        const {
            userPathwayComplete: { badges = [] }
        } = this.state;

        return (
            <div className={'container'}>
                <BadgesSection userBadges={badges} isPreview={false} isPathway={true} />
            </div>
        );
    };

    render() {
        const {
            userPathway = {},
            pathway = {},
            viewablePathways = [],
            loggedInUserCompany,
            userPathways,
            history
        } = this.props;
        const { loading, modalOpen } = this.state;
        if (loading) {
            return <FullPageLoadingIndicator />;
        }

        return (
            <Page className={'pathway-interstitial'}>
                <div className={'col-sm-12'}>
                    {this._renderCongrats()}
                    {this._renderPathwayStats()}
                    {this._renderPathwayAssociatedBadges()}
                    <RecommendedPathwaysSection
                        pathway={pathway}
                        company={loggedInUserCompany}
                        userPathway={userPathway}
                        userPathways={userPathways}
                        pathwayOptions={viewablePathways}
                        loggedInUserCompany={loggedInUserCompany}
                        history={history}
                    />
                </div>
            </Page>
        );
    }
}

export const makeMapStateToProps = () => {
    const getPathwaysFromStateById = makeGetObjectsFromStateByFunction('pathways.byId');
    const getUserPathwaysFromStateById = makeGetObjectsFromStateById('userPathways.byId');
    const mapStateToProps = (state, ownProps) => {
        const {
            pathways,
            userPathways,
            auth: { loggedInUser, loggedInUserCompanyId, isVerbAdmin, isAccountOwner },
            userCollections: { loading },
            companies,
            teams
        } = state;
        const {
            match: {
                params: { userPathwayId }
            }
        } = ownProps;

        const loggedInUserCompany = (companies.byId && companies.byId[loggedInUserCompanyId]) || {};
        const companyPathwayLicenseLookup = (loggedInUserCompany.pathwayLicenses || []).reduce((acc, curr) => {
            acc[curr.pathwayId] = curr;
            return acc;
        }, {});

        const companyPathwayOptions = (loggedInUserCompany.pathwayOptions || []).reduce((acc, curr) => {
            acc.push(curr.id);
            return acc;
        }, []);

        const userPathwaysByPathwayId = Object.values(userPathways.byId).reduce((acc, userPathway) => {
            if (userPathway.pathwayId) {
                acc[userPathway.pathwayId] = userPathway;
            }
            return acc;
        }, {});

        const filterFunction = (pathway) => {
            if (
                !pathway.isActive ||
                (pathway._id in userPathwaysByPathwayId &&
                    (userPathwaysByPathwayId[pathway._id].status === 'COMPLETED' ||
                        userPathwaysByPathwayId[pathway._id].isCompleted))
            ) {
                return false;
            }

            if (companyPathwayOptions.includes(pathway._id)) {
                if (
                    !pathway.subscriptionType ||
                    (pathway.subscriptionType || '').toLocaleUpperCase() === LICENSE_TYPES.AVAILABLE_FOR_ALL_UPPER
                ) {
                    return true;
                }
                if (
                    (pathway.subscriptionType || '').toLocaleUpperCase() ===
                    LICENSE_TYPES.EXCLUSIVE_COMPANY_LICENSE_UPPER
                ) {
                    return pathway._id in companyPathwayLicenseLookup;
                }
            }
            return false;
        };

        const viewablePathways = getPathwaysFromStateById(state, filterFunction);
        const userPathway = userPathways.byId[userPathwayId];
        const pathway = pathways.byId[userPathway && userPathway.pathwayId];
        const userPathwaysArray = getUserPathwaysFromStateById(state);

        return {
            pathway,
            userPathway,
            pathwayLoading: userPathways.loading,
            collectionsLoading: loading,
            loggedInUser,
            loggedInUserCompany,
            isVerbAdmin,
            isAccountOwner,
            viewablePathways,
            teamsById: teams.byId,
            userPathways: userPathwaysArray
        };
    };
    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    getUserPathway: (params) => dispatch(getUserPathway(params)),
    getBadge: (params) => dispatch(getBadge(params)),
    getPathway: (params) => dispatch(getPathway(params)),
    getManyPathways: (params) => dispatch(getManyPathways(params)),
    getManyUserPathways: (params) => dispatch(getManyUserPathways(params)),
    getManyTeams: (params) => dispatch(getManyTeams(params)),
    putUserPathway: (params) => dispatch(putUserPathway(params))
});

export default connect(makeMapStateToProps, mapDispatchToProps)(PathwayInterstitial);
